import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import { useTheme, styled, keyframes } from '@mui/material/styles';
import { useMediaQuery, Divider, Drawer, Grid, Box, Typography } from '@mui/material';

// third party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project import
import MenuList from './MenuList';
import { drawerWidth } from 'config.js';

// assets
import SidebarImage from 'assets/images/eurekalogo.png'; 

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`; 

// custom style
const Nav = styled((props) => <nav {...props} />)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: drawerWidth,
    flexShrink: 0
  }
}));

// ==============================|| SIDEBAR ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
  const theme = useTheme();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const drawer = (
    <>
      <Box sx={{ display: { md: 'none', xs: 'block' } }}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          elevation={5}
          alignItems="center"
          spacing={0}
          sx={{
            ...theme.mixins.toolbar,
            lineHeight: 0,
            background: theme.palette.primary.main,
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)'
          }}
        >
          <Grid item>
            <img src={SidebarImage} alt="Logo" /> 
          </Grid>
        </Grid>
      </Box>
      <Divider />
      
      {/* Modern Sidebar Content */}
      <Box sx={{ 
        position: 'relative',
        height: '100%',
        background: theme.palette.mode === 'dark' 
          ? theme.palette.background.paper
          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        overflow: 'hidden'
      }}>
        {/* Premium Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #1976d2, #42a5f5, #1976d2)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 3s ease-in-out infinite`
        }} />
        
        {/* Geometric Patterns */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          right: '8%',
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.15), rgba(66, 165, 245, 0.15))',
          animation: `${float} 4s ease-in-out infinite`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            borderRadius: '50%',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            animation: `${float} 4s ease-in-out infinite reverse`
          }
        }} />
        
        <Box sx={{
          position: 'absolute',
          top: '35%',
          right: '3%',
          width: 8,
          height: 8,
          borderRadius: '2px',
          background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.12), rgba(66, 165, 245, 0.12))',
          animation: `${float} 5s ease-in-out infinite reverse`,
          transform: 'rotate(45deg)'
        }} />
        
        <Box sx={{
          position: 'absolute',
          top: '55%',
          right: '12%',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.1))',
          animation: `${float} 6s ease-in-out infinite`,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            borderRadius: '50%',
            border: '1px solid rgba(25, 118, 210, 0.08)'
          }
        }} />
        
        <Box sx={{
          position: 'absolute',
          top: '75%',
          right: '6%',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.08), rgba(66, 165, 245, 0.08))',
          animation: `${float} 7s ease-in-out infinite reverse`
        }} />
        
        {/* Subtle Grid Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(25, 118, 210, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(25, 118, 210, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
          opacity: 0.3
        }} />
        
        {/* Sidebar Header */}
        <Box sx={{ 
          p: 3, 
          pb: 2,
          borderBottom: '1px solid rgba(25, 118, 210, 0.1)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 2,
            background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
            borderRadius: 1
          }
        }}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <img 
              src={SidebarImage} 
              alt="Eureka Group Logo" 
              style={{
                maxWidth: '140px',
                maxHeight: '45px',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ 
            color: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.87)' 
              : '#1976d2', 
            fontWeight: 'bold',
            fontSize: '1.1rem',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -2,
              left: 0,
              width: 30,
              height: 2,
              background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
              borderRadius: 1
            }
          }}>
            Dashboard
          </Typography>
        </Box>

        <PerfectScrollbar style={{ 
          height: 'calc(100vh - 140px)', 
          padding: '16px 0',
          position: 'relative',
          zIndex: 1
        }}>
        <MenuList />
      </PerfectScrollbar>
      </Box>
    </>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Nav>
      <Drawer
        container={container}
        variant={matchUpMd ? 'persistent' : 'temporary'}
        anchor="left"
        open={drawerOpen}
        onClose={drawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            borderRight: 'none',
            boxShadow: '0 0.15rem 1.75rem 0 rgba(33, 40, 50, 0.15)',
            top: { md: 64, sm: 0 }
          }
        }}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </Nav>
  );
};

Sidebar.propTypes = {
  drawerOpen: PropTypes.bool,
  drawerToggle: PropTypes.func,
  window: PropTypes.object
};

export default Sidebar;